'use client';

import { FC, useContext, useMemo } from 'react';
import { LeftNavEntitySelectorProps } from './LeftNavEntitySelector.types';
import {
  Flex,
  Text,
  Button,
  ButtonGroup,
  Separator,
  LeftNavItem,
} from '@/_components';
import styles from './LeftNavEntitySelector.module.css';
import { UserContext } from '@/_contexts/UserProvider';
import { LeftNavEntityList } from './_components/LeftNavEntityList';
import { getClasses } from '@/_utils/classnames';
import { ClientConversationsContext } from '@/_contexts/client/ClientConversationsProvider';
import { UserPreviewContext } from '@/_contexts/admin/UserPreviewProvider';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { Company } from '@/_types/models/company';
import { useParams } from 'next/navigation';

export const LeftNavEntitySelector: FC<LeftNavEntitySelectorProps> = ({
  isOpen,
  setOpen,
}) => {
  const { companies, selectedCompanyId } = useContext(UserContext);
  const { currentEntityUnseenCount, unseenCount, entityUnseenCounts } =
    useContext(ClientConversationsContext);
  const { previewUserCompanies, previewCompany, previewUserCompanyId } =
    useContext(UserPreviewContext);
  const { preferences } = useContext(FirmSettingsContext);

  const { previewUserId } = useParams();
  const isNewDashboardEnabled = preferences?.tmp_new_client_dashboard;

  const dashboardCompanies: Array<Company> = useMemo(
    () => (!!previewUserId ? previewUserCompanies : companies),
    [previewUserId, companies, previewUserCompanies],
  );

  const dashboardSelectedCompanyId: number | null | undefined = useMemo(
    () => (!!previewUserId ? previewUserCompanyId : selectedCompanyId),
    [previewUserId, previewUserCompanyId, selectedCompanyId],
  );

  const dashboardCompany: Company | undefined = !!previewUserId
    ? previewCompany
    : companies?.find((c) => c.id === dashboardSelectedCompanyId);

  const hasMoreCompanies: boolean = dashboardCompanies?.length > 1;
  const unseenDifference: number = unseenCount - currentEntityUnseenCount;

  if (!hasMoreCompanies) return null;

  const leftNavEntitySelectorClasses: string = getClasses(
    styles.leftNavEntitySelector__currentEntity,
  );

  const onToggleEntities = () => setOpen(!isOpen);

  // If new dashboard is enabled, show static display
  if (isNewDashboardEnabled) {
    return (
      <>
        <Flex
          isColumn={true}
          className={styles.newLeftNavEntitySelector__currentEntity}
          data-id="LeftNavEntitySelector.Selector"
          gap={4}
        >
          <Text type="tiny" color="secondary">
            Current Entity
          </Text>
          <Text type="small" fontWeight="medium" color="primary">
            {dashboardCompany?.name}
          </Text>
        </Flex>
        <Separator isLight={true} />
      </>
    );
  }

  // Original dropdown functionality for old dashboard
  return (
    <>
      <Flex
        isColumn={true}
        className={leftNavEntitySelectorClasses}
        data-id="LeftNavEntitySelector.Selector"
      >
        <Text type="micro" color="secondary">
          Current Entity
        </Text>
        <Flex isBetween={true} isYCentered={true} onClick={onToggleEntities}>
          <Text type="small" fontWeight="medium" isEllipsis={true}>
            {dashboardCompany?.name}
          </Text>
          <ButtonGroup>
            <Button
              value="Select Entity"
              iconLeft={isOpen ? 'caret-up' : 'caret-down'}
              intent="ghost"
              notificationCount={unseenDifference}
              isIconOnly={true}
              size="tiny"
            />
          </ButtonGroup>
        </Flex>
      </Flex>
      <div onClick={onToggleEntities} data-id="LeftNavEntitySelector.Switch">
        <LeftNavItem name="Entity" icon="switch" />
      </div>
      {isOpen && (
        <LeftNavEntityList
          companies={dashboardCompanies}
          selectedCompanyId={dashboardSelectedCompanyId}
          entityUnseenCounts={entityUnseenCounts}
        />
      )}
      <Separator isLight={true} />
    </>
  );
};
